import React from 'react';
import { GraduationCap, BookOpen, Award, Calendar } from 'lucide-react';

const Education = () => {
  const educationData = [
    {
      degree: "BS Computer Science",
      institution: "FAST-NUCES",
      duration: "2023 - Present",
      status: "6th Semester",
      description: "Currently pursuing Bachelor's degree in Computer Science with focus on software engineering, data structures, algorithms, and modern web development.",
      highlights: [
        "Strong foundation in Data Structures & Algorithms",
        "Object-Oriented Programming (C++)",
        "Database Management Systems",
        "Software Engineering Principles",
        "Web Development Technologies",
        "AI & Machine Learning Concepts",
        "Data Analysis & Visualization"
      ],
      grade: "Current GPA: 3+",
      icon: <GraduationCap className="w-8 h-8" />,
      color: "from-cyan-500 to-blue-600"
    },
    {
      degree: "O & A Levels",
      institution: "Divisional Model College",
      duration: "2018 - 2023",
      status: "Completed",
      description: "Completed Cambridge International curriculum with focus on Mathematics, Physics, Chemistry, and Computer Science.",
      highlights: [
        "Strong mathematical foundation",
        "Physics & Chemistry fundamentals",
        "Introduction to Computer Science",
        "English Language & Literature",
        "Critical thinking and problem-solving"
      ],
      grade: "O Levels: 2A*'s, 6A's | A Levels: 1A, 2B", 
      icon: <BookOpen className="w-8 h-8" />,
      color: "from-purple-500 to-pink-600"
    }
  ];

  const certifications = [
    {
      title: "MERN Stack Development",
      issuer: "Self-Taught & Project-Based",
      year: "2025",
      skills: ["MongoDB", "Express.js", "React.js", "Node.js"]
    },
    {
      title: "Frontend Development",
      issuer: "Various Online Platforms",
      year: "2024",
      skills: ["HTML", "CSS", "JavaScript", "TailwindCSS"]
    },
    {
      title: "C++ Programming",
      issuer: "University Coursework",
      year: "2023",
      skills: ["OOP", "Data Structures", "STL", "Algorithms"]
    }
  ];

  return (
    <section id="education" className="py-20 bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Education & <span className="text-cyan-400">Learning</span>
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-purple-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Academic foundation and continuous learning journey
            </p>
          </div>

          {/* Education Timeline */}
          <div className="space-y-12 mb-16">
            {educationData.map((edu, index) => (
              <div 
                key={index}
                className="relative"
              >
                {/* Timeline Line */}
                {index < educationData.length - 1 && (
                  <div className="absolute left-6 md:left-12 top-20 w-0.5 h-32 bg-gradient-to-b from-cyan-400 to-purple-500"></div>
                )}
                
                <div className="grid md:grid-cols-12 gap-8 items-start">
                  {/* Timeline Icon */}
                  <div className="md:col-span-2 flex justify-start md:justify-center">
                    <div className={`p-4 bg-gradient-to-r ${edu.color} rounded-full shadow-lg`}>
                      <div className="text-white">
                        {edu.icon}
                      </div>
                    </div>
                  </div>

                  {/* Education Content */}
                  <div className="md:col-span-10">
                    <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-gray-600 transition-all duration-300">
                      {/* Header */}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                        <div>
                          <h3 className="text-2xl font-bold text-white mb-2">{edu.degree}</h3>
                          <p className="text-cyan-400 font-semibold text-lg">{edu.institution}</p>
                        </div>
                        <div className="mt-4 md:mt-0 text-right">
                          <div className="flex items-center text-gray-300 mb-2">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>{edu.duration}</span>
                          </div>
                          <span className="bg-gradient-to-r from-cyan-500 to-purple-600 text-white px-3 py-1 rounded-full text-sm">
                            {edu.status}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                        {edu.description}
                      </p>

                      {/* Highlights */}
                      <div className="mb-6">
                        <h4 className="text-white font-semibold mb-4">Key Learning Areas:</h4>
                        <div className="grid md:grid-cols-2 gap-3">
                          {edu.highlights.map((highlight, hIndex) => (
                            <div key={hIndex} className="flex items-center space-x-3">
                              <div className="w-2 h-2 bg-cyan-400 rounded-full flex-shrink-0"></div>
                              <span className="text-gray-300">{highlight}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Grade */}
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <div className="flex items-center">
                          <Award className="w-5 h-5 text-yellow-400 mr-2" />
                          <span className="text-yellow-400 font-semibold">{edu.grade}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Certifications & Skills */}
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Certifications & <span className="text-cyan-400">Skills Development</span>
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              {certifications.map((cert, index) => (
                <div 
                  key={index}
                  className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-cyan-400/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-white font-semibold text-lg mb-1">{cert.title}</h4>
                    <p className="text-gray-400 text-sm mb-2">{cert.issuer}</p>
                    <span className="text-cyan-400 text-sm font-medium">{cert.year}</span>
                  </div>
                  
                  <div className="space-y-2">
                    {cert.skills.map((skill, sIndex) => (
                      <div 
                        key={sIndex}
                        className="bg-gray-700/50 text-gray-300 text-xs px-3 py-1 rounded-full text-center"
                      >
                        {skill}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Current Learning Goals */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-white mb-6">
              Current Learning <span className="text-cyan-400">Focus</span>
            </h3>
            <div className="grid md:grid-cols-4 gap-4">
              {["Generative AI", "Advanced React Patterns", "NextJS", "Backend Development"].map((goal, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-r from-gray-800/50 to-gray-700/50 backdrop-blur-sm rounded-lg p-4 border border-gray-600 hover:border-cyan-400/50 transition-all duration-300"
                >
                  <span className="text-gray-300 font-medium">{goal}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;
